import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { loginPathFor } from '../lib/nextPath.js';
import { bootstrapSession } from '../lib/session.js';
import { useSession } from '../lib/useSession.js';

/**
 * Porta das telas que exigem sessão.
 *
 * Manda para `/entrar` guardando para onde a pessoa queria ir — quem abre o QR
 * no celular cai em `/parear?c=ABC123`, e perder esse código no caminho do
 * login significaria voltar para a TV e ler o QR de novo.
 */
export function RequireAuth({
  children,
  /** Papel mínimo. Quem não tem é mandado para /inicio, não para o login. */
  minimumRole,
}: {
  children: ReactNode;
  minimumRole?: 'OWNER' | 'ADMIN';
}) {
  const session = useSession();
  const location = useLocation();

  // O token vive em memória e some a cada recarga; o cookie de refresh, não.
  // Perguntar ao servidor ANTES de decidir é o que impede a recarga no meio
  // do jogo de jogar o operador na tela de login.
  useEffect(() => {
    void bootstrapSession();
  }, []);

  // Mesmo fundo da tela de login, para a espera não piscar branco.
  if (!session.ready) {
    return (
      <main className="auth" aria-busy="true">
        <p className="auth-sub">Entrando…</p>
      </main>
    );
  }

  if (!session.signedIn) {
    return <Navigate to={loginPathFor(location)} replace />;
  }

  if (minimumRole) {
    const allowed =
      minimumRole === 'ADMIN'
        ? session.activeRole === 'ADMIN' || session.activeRole === 'OWNER'
        : session.activeRole === 'OWNER';

    if (!allowed) return <Navigate to="/inicio" replace />;
  }

  return <>{children}</>;
}
